import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Check, Zap, Crown, ArrowLeft, Loader2, CheckCircle2, XCircle, BadgeCheck, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import AuthModal from '../components/AuthModal';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import { useBrandSettings } from '../context/BrandSettingsContext';
import BrandLogo from '../components/BrandLogo';

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [user, setUser] = useState(null);
  const { getPaymentUrl, payment_plans } = useBrandSettings();

  // Check for payment status on page load
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus({
        type: 'cancelled',
        message: 'Payment was cancelled. Feel free to try again when ready.'
      });
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  // Poll payment status after successful checkout
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus({
        type: 'pending',
        message: 'Payment is being processed. Please check your email for confirmation.'
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.getPaymentStatus(sessionId);

      if (response.payment_status === 'paid') {
        setPaymentStatus({
          type: 'success',
          message: response.message,
          tier: response.subscription_tier
        });

        // Update local user data
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const updatedUser = {
            ...JSON.parse(storedUser),
            subscription_tier: response.subscription_tier
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        }

        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoading(false);
        return;
      } else if (response.status === 'expired') {
        setPaymentStatus({
          type: 'error',
          message: 'Payment session expired. Please try again.'
        });
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoading(false);
        return;
      }

      // Continue polling if payment still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus({
        type: 'error',
        message: 'Error checking payment status. Please contact support if charged.'
      });
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (plan === 'free') {
      navigate('/');
      return;
    }

    // Vendor-agnostic: any admin-configured paid plan → redirect to its payment link.
    // Works with Stripe checkout URLs, PayPal.me, eZeePayments, Lumin, Razorpay, etc.
    const paymentUrl = getPaymentUrl(plan, 'monthly');
    if (paymentUrl) {
      window.location.href = paymentUrl;
      return;
    }

    // No admin URL configured → fall back to embedded Stripe checkout (if any legacy
    // package_id matches SUBSCRIPTION_PACKAGES on the backend). Preserved for
    // backward compatibility with the original Stripe flow.
    setSelectedPlan(plan);
    const storedUser = localStorage.getItem('user');

    if (!storedUser) {
      setShowAuthModal(true);
      return;
    }

    await initiateCheckout(plan);
  };

  const initiateCheckout = async (packageId) => {
    try {
      setLoadingPlan(packageId);
      
      const response = await apiService.createCheckoutSession(packageId);
      
      // Redirect to Stripe Checkout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to start checkout. Please try again.'
      });
      setLoadingPlan(null);
    }
  };

  const handleAuthSuccess = async (newUser) => {
    setUser(newUser);
    setShowAuthModal(false);
    
    // If a plan was selected, proceed to checkout
    if (selectedPlan && selectedPlan !== 'free') {
      await initiateCheckout(selectedPlan);
    }
  };

  // Always-visible Free tier (system plan, not admin-configurable)
  const freePlan = {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    icon: Shield,
    color: 'from-slate-500 to-slate-600',
    features: [
      '5 verifications per month',
      'Text, image & video claims',
      'AI-powered analysis',
      'Share results',
      'No ads'
    ],
    limitations: [
      'No persistent history',
      'No data export'
    ]
  };

  // Pick an icon for a given tier — falls back gracefully for any admin-defined tier.
  const iconForTier = (tier) => {
    const t = (tier || '').toLowerCase();
    if (t === 'enterprise' || t === 'team') return Crown;
    return Zap;
  };

  // Pick a gradient for the paid plan card. `display_order === 1` (or the sole
  // paid plan) gets the highlighted blue treatment; others get muted styles.
  const colorForPaidPlan = (plan, index) => {
    if (plan.badge || index === 0) return 'from-blue-600 to-blue-700';
    return 'from-indigo-500 to-indigo-700';
  };

  // Build the visible plan list: Free first, then every active admin-configured plan.
  const paidPlans = (payment_plans || []).map((p, index) => ({
    id: p.plan_id,
    name: p.plan_name,
    price: p.price || '',
    period: p.period || '',
    icon: iconForTier(p.tier),
    color: colorForPaidPlan(p, index),
    badge: p.badge || null,
    features: p.features || [],
    limitations: p.limitations || [],
    finePrint: p.fine_print || null,
    isHighlighted: !!p.badge || index === 0,
  }));

  const plans = [freePlan, ...paidPlans];

  const isPremium = user && (user.subscription_tier === 'pro' || user.subscription_tier === 'premium' || user.subscription_tier === 'enterprise');
  const currentTier = user?.subscription_tier || 'free';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 sm:gap-2 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors duration-200 text-xs sm:text-sm dark:border-slate-600 dark:text-slate-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-16">
        {/* Payment Status Alert */}
        {paymentStatus && (
          <div className="max-w-2xl mx-auto mb-8">
            <Alert 
              variant={paymentStatus.type === 'error' ? 'destructive' : 'default'}
              className={
                paymentStatus.type === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300' 
                  : paymentStatus.type === 'cancelled'
                  ? 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-800 dark:text-yellow-300'
                  : ''
              }
            >
              {paymentStatus.type === 'success' && <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
              {paymentStatus.type === 'error' && <XCircle className="h-4 w-4" />}
              {paymentStatus.type === 'cancelled' && <XCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />}
              <AlertDescription>{paymentStatus.message}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Processing Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 text-center shadow-2xl mx-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Processing Payment</h3>
              <p className="text-slate-600 dark:text-slate-400 mt-2">Please wait while we confirm your subscription...</p>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 dark:from-blue-400 dark:via-blue-500 dark:to-blue-600 bg-clip-text text-transparent">
            Choose Your Plan
          </h1>
          <p className="text-base sm:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto px-4">
            Access Kai's powerful verification with flexible pricing options.
            Upgrade anytime to unlock premium features.
          </p>
          {isPremium && (
            <Badge className="mt-4 bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              You have {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} access
            </Badge>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto mb-12 sm:mb-16">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentTier === plan.id || (isPremium && plan.id === currentTier);
            const isUpgrade = plan.id !== 'free' && currentTier === 'free';
            
            return (
              <Card
                key={plan.id}
                className={`relative hover:shadow-2xl transition-all duration-300 bg-white dark:bg-slate-800 ${
                  plan.isHighlighted ? 'border-blue-500 border-2 md:scale-105' : 'border-slate-200 dark:border-slate-700'
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
                data-testid={`pricing-plan-card-${plan.id}`}
              >
                {plan.badge && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1">
                    {plan.badge}
                  </Badge>
                )}
                {isCurrentPlan && (
                  <Badge className="absolute -top-3 right-4 bg-green-600 text-white px-4 py-1">
                    Current Plan
                  </Badge>
                )}
                <CardHeader className="text-center pb-6 sm:pb-8">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${plan.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl sm:text-2xl mb-2 dark:text-white">{plan.name}</CardTitle>
                  <div className="mb-2">
                    <span className="text-3xl sm:text-4xl font-bold dark:text-white">{plan.price}</span>
                    {plan.period !== 'forever' && (
                      <span className="text-slate-500 dark:text-slate-400 ml-2">{plan.period}</span>
                    )}
                  </div>
                  {plan.finePrint && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                      {plan.finePrint}
                    </p>
                  )}
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isCurrentPlan || loadingPlan === plan.id}
                    data-testid={`pricing-plan-cta-${plan.id}`}
                    className={`w-full ${
                      isCurrentPlan
                        ? 'bg-green-600 hover:bg-green-600 cursor-default'
                        : plan.isHighlighted
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-slate-600 hover:bg-slate-700'
                    }`}
                  >
                    {loadingPlan === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Current Plan
                      </>
                    ) : plan.id === 'free' ? (
                      'Get Started'
                    ) : isUpgrade ? (
                      'Upgrade Now'
                    ) : (
                      `Get ${plan.name} Access`
                    )}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Features:</p>
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{feature}</span>
                      </div>
                    ))}
                    {plan.limitations && plan.limitations.length > 0 && (
                      <>
                        <div className="border-t border-slate-200 dark:border-slate-700 my-3"></div>
                        {plan.limitations.map((limitation, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <X className="w-5 h-5 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-500 dark:text-slate-400">{limitation}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Influencer Certification Banner */}
        <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
          <Card className="border-2 border-amber-400 dark:border-amber-600 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 overflow-hidden">
            <CardContent className="p-4 sm:p-8">
              <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <BadgeCheck className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    KaiCheckIt Influencer Certification
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-3">
                    Are you an influencer? Get verified and display the official KaiCheckIt badge on your profile to build trust with your audience.
                  </p>
                  <div className="flex flex-wrap gap-3 sm:gap-4 justify-center md:justify-start text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <strong>$20</strong>/month
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <strong>$200</strong>/year
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <strong className="text-green-600">$120</strong>/year (first 50!)
                    </span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/influencer')}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg flex-shrink-0 w-full md:w-auto"
                >
                  <BadgeCheck className="w-5 h-5 mr-2" />
                  Get Certified
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feature Comparison */}
        <div className="max-w-4xl mx-auto">
          <Card className="p-4 sm:p-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8 dark:text-white">Why Upgrade to Premium?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-base sm:text-lg text-slate-800 dark:text-slate-200 mb-3">Free Plan Includes:</h3>
                <div className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Text & image verification</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Explore Other FactCheckers</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Session-only history</span>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-base sm:text-lg text-slate-800 dark:text-slate-200 mb-3">Premium Unlocks:</h3>
                <div className="flex items-start gap-2">
                  <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400"><strong className="dark:text-slate-200">Unlimited Checks</strong> - No monthly limits</span>
                </div>
                <div className="flex items-start gap-2">
                  <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400"><strong className="dark:text-slate-200">Persistent History</strong> - Access anytime</span>
                </div>
                <div className="flex items-start gap-2">
                  <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400"><strong className="dark:text-slate-200">Priority Support</strong> - Faster responses</span>
                </div>
                <div className="flex items-start gap-2">
                  <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600 dark:text-slate-400"><strong className="dark:text-slate-200">Export Reports</strong> - Download your checks</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Security Badge */}
        <div className="text-center mt-8 sm:mt-12">
          <div className="inline-flex items-center gap-2 bg-white dark:bg-slate-800 rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-md border border-slate-200 dark:border-slate-700">
            <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Secure payments — cancel anytime</span>
          </div>
        </div>
      </main>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default PricingPage;
